import { Component, Inject, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Sort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BehaviorSubject } from 'rxjs';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { EsraPipingAPIClient, CommonNoteViewModel } from 'src/app/shared/models/autogenerated-piping';

export interface NoteOption {
  id: string;
  code: string;
  note: string;
  category?: string;
  selected?: boolean;
  readonly?: boolean;
}

export interface NotesSelectionData {
  availableNotes: NoteOption[];
  selectedNotes?: NoteOption[]; // Notes already selected
  title?: string; // Custom title for the modal
  readOnlyNotes?: NoteOption[];
  subtitle?: string;
}

@Component({
  selector: 'app-notes-selection-modal',
  templateUrl: './notes-selection-modal.component.html',
  styleUrls: ['./notes-selection-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatCheckboxModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FontAwesomeModule
  ]
})
export class NotesSelectionModalComponent implements OnInit {
  faSearch = faSearch;
  displayedColumns: string[] = ['select', 'note_code', 'note_description'];
  dataSource = new MatTableDataSource<NoteOption>([]);

  @ViewChild(MatPaginator, { static: true }) matPaginator!: MatPaginator;

  searchQuery = '';
  sortBy: string = 'note_code';
  sortDirection: 'asc' | 'desc' = 'asc';
  pageSize: number = 10;
  pageIndex: number = 0;
  totalRecords: number = 0;
  isSearching$ = new BehaviorSubject<boolean>(false);
  searchResults: NoteOption[] = [];
  selectedNotes: NoteOption[] = [];
  modalTitle: string = 'Select Notes'; // Default title

  constructor(
    public dialogRef: MatDialogRef<NotesSelectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NotesSelectionData,
    private esraPipingAPIClient: EsraPipingAPIClient
  ) {
    // Initialize search results with data or empty array
    this.searchResults = data?.availableNotes || [];
    this.totalRecords = this.searchResults.length;

    // Initialize selected notes with existing selections
    this.selectedNotes = data?.selectedNotes ? [...data.selectedNotes] : [];

    // Set custom title if provided
    this.modalTitle = data?.title || 'Select Notes';

    this.updateSearchResults();
  }

  ngOnInit(): void {
    this.loadNotes();
  }

  loadNotes(): void {
    const indicator = indicate(this.isSearching$);

    this.esraPipingAPIClient.getAllCurrentCommonNotes()
    .pipe(
      first(),
      tap((notes: CommonNoteViewModel[]) => {
        // Map CommonNoteViewModel to NoteOption
        const noteOptions: NoteOption[] = (notes || []).map((note: CommonNoteViewModel) => ({
          id: note.noteId || '',
          code: note.codeId || '',
          note: note.note || '',
          category: 'Common', // Use same category as in piping-summary component
          selected: false
        }));

        this.searchResults = noteOptions;
        this.totalRecords = noteOptions.length;

        // Maintain existing selections by matching IDs
        this.syncSelectedNotes();

        this.updateSearchResults();
      }),
      indicator
    )
    .subscribe({
      error: (error) => {
        // Error loading notes, use fallback
        if (this.data?.availableNotes) {
          this.searchResults = this.data.availableNotes;
          this.totalRecords = this.searchResults.length;
          this.syncSelectedNotes();
          this.updateSearchResults();
        }
      }
    });
  }

  syncSelectedNotes(): void {
    // DON'T filter out notes - we want to keep all pre-selected notes
    // even if they're not currently in the search results

    // Just ensure the selected flag is properly set
    this.selectedNotes.forEach(selectedNote => {
      selectedNote.selected = true;
    });
  }

  updateSearchResults(): void {
    let filteredResults = [...this.searchResults];

    // Apply search filter
    if (this.searchQuery && this.searchQuery.trim()) {
      const searchLower = this.searchQuery.toLowerCase().trim();
      filteredResults = this.searchResults.filter(note =>
        (note.code && note.code.toLowerCase().includes(searchLower)) ||
        (note.note && note.note.toLowerCase().includes(searchLower)) ||
        (note.category && note.category.toLowerCase().includes(searchLower))
      );
    }

    // Apply sorting
    filteredResults.sort((a, b) => {
      let aVal: string;
      let bVal: string;

      switch (this.sortBy) {
        case 'note_description':
          aVal = a.note || '';
          bVal = b.note || '';
          break;
        default: // note_code
          aVal = a.code || '';
          bVal = b.code || '';
          break;
      }

      const comparison = aVal.localeCompare(bVal);
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    // Apply pagination locally
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const paginatedResults = filteredResults.slice(startIndex, endIndex);

    // Update data source with paginated results
    this.dataSource.data = paginatedResults;
    this.totalRecords = filteredResults.length;
  }

  onSearchChange(event: any): void {
    this.searchQuery = event.target.value || '';
    this.pageIndex = 0; // Reset to first page
    this.updateSearchResults();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateSearchResults();
  }

  sortData(sort: Sort): void {
    this.sortBy = sort.active || 'note_code';
    this.sortDirection = sort.direction as 'asc' | 'desc' || 'asc';
    this.pageIndex = 0; // Reset to first page
    this.updateSearchResults();
  }

  goToPage(page: number): void {
    this.pageIndex = page;
    this.updateSearchResults();
  }

  get hasSelections(): boolean {
    // Always allow saving - user should be able to save empty selection to remove all notes
    return true;
  }

  checkboxChanged(checked: boolean, note: NoteOption): void {
    if (note.readonly) {
      return;
    }

    if (checked) {
      // Check if note is already selected using the same logic as isChecked
      const alreadySelected = this.selectedNotes.some(s =>
        s.id === note.id ||
        (s.code && note.code && s.code.trim().toLowerCase() === note.code.trim().toLowerCase())
      );

      if (!alreadySelected) {
        this.selectedNotes.push({ ...note, selected: true });
      }
    } else {
      this.selectedNotes = this.selectedNotes.filter(s =>
        s.id !== note.id &&
        !(s.code && note.code && s.code.trim().toLowerCase() === note.code.trim().toLowerCase())
      );
    }
  }

  isChecked(note: NoteOption): boolean {
    // Check by ID first, then by code as fallback
    return this.selectedNotes.some(s =>
      s.id === note.id ||
      (s.code && note.code && s.code.trim().toLowerCase() === note.code.trim().toLowerCase())
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    // Always return the selected notes array, even if empty
    this.dialogRef.close(this.selectedNotes);
  }
}
